import type { DrugEventPrestationType, DrugEventType, RawCategoricalLabResult, RawDrugEvent, RawHealthConstant, RawLabResult } from '@sancare/typescript-commons/src/types/health-data'

import Api from '@/misc/api'
import type { Commit, CurrentSearchLike, DrugDocumentItem, HealthConstantDocumentItem, LabResultDocumentItem, ListStayDocumentsResponse, StayDocumentEntries, TextualChunk, TextualDocumentItem, TextualLabResultDocumentItem } from '@/misc/types'
import type { Chunk } from '@/stay-displayer/health-data/types'
import { buildKeywordSearchParams } from '@/text-processing/utils'

function rebuildContent(chunks: TextualChunk[]): string {
  return chunks.map((chunk) => chunk.text).join('')
}

// The backend already gives an exact offsetStart per chunk, so chunkEnd/hash need no client-side
// stringHash-based positional guessing.
function buildChunks(chunks: TextualChunk[]): Chunk[] {
  return chunks.map((chunk) => ({
    string: chunk.text,
    chunkStart: chunk.offsetStart,
    chunkEnd: chunk.offsetStart + chunk.text.length,
    hash: String(chunk.offsetStart),
    color: chunk.color,
    isHighlighted: chunk.color !== null,
    isNegative: chunk.isNegative,
    isDoubt: chunk.isDoubt,
    isFalseJustification: chunk.isFalseJustification,
    hasSearchMatch: chunk.hasSearchMatch,
    origins: chunk.origins,
  }))
}

// /documents is the source of truth for id/title/type/date too, not just content — stay_full only
// still sends falseJustifications for reports/textualHealthEntries.
function applyDocumentIdentity<T extends { generatedDataId?: string | null }>(
  entries: T[],
  documents: TextualDocumentItem[],
  mapExtraFields: (doc: TextualDocumentItem) => Record<string, unknown>
): T[] {
  const documentsByCrcs = new Map(documents.map((doc) => [doc.crcs, doc]))

  return entries.map((entry) => {
    const doc = documentsByCrcs.get(entry.generatedDataId ?? '')

    return doc
      ? { ...entry, id: doc.sancareId, content: rebuildContent(doc.content), ...mapExtraFields(doc) }
      : { ...entry, content: '' }
  })
}

// The stay fetch no longer sends drugEvents/healthConstants/labResults/categoricalLabResults at all
// (see Domain.Stay.Stay.yml/Entity.FrSmr.Stay.yml) — the /documents response is now their only
// source, so these are fully replaced rather than merged. Field renames (sancareId -> id, date ->
// creationDate) and the drug/constantType wrapping match what the rest of the app already expects
// (formatDrugEvent, ApiJustificationSummary.vue...).
export function mapDrugDocumentItem(item: DrugDocumentItem): RawDrugEvent {
  return {
    id: item.sancareId,
    amount: item.amount,
    creationDate: item.date as string,
    drug: {
      shortUcd: item.ucd,
      commercialName: item.commercialName,
      atc: item.atc,
      atcLabel: item.atcLabel,
      activeSubstance: item.activeSubstance,
    },
    // Wire format is a plain string (backend enum's ->value) — the app-level enums below share the
    // same values, but nothing validates that at the type level, hence the explicit casts.
    type: item.type as DrugEventType,
    unit: item.unit,
    prestationType: item.prestationType as DrugEventPrestationType | null,
    prescriptionValidation: item.prescriptionValidation,
    indication: item.indication,
  }
}

export function mapHealthConstantDocumentItem(item: HealthConstantDocumentItem): RawHealthConstant {
  return {
    id: item.sancareId,
    creationDate: item.date as string,
    constantType: { description: item.title },
    value: item.value,
    unit: item.unit,
    documentSourceIdentifier: item.documentSourceIdentifier,
  }
}

export function mapLabResultDocumentItem(item: LabResultDocumentItem): RawLabResult {
  return {
    id: item.sancareId,
    creationDate: item.date as string,
    code: item.code,
    conclusion: item.conclusion,
    title: item.title,
    value: item.value,
    unit: item.unit,
    documentSourceIdentifier: item.documentSourceIdentifier,
  }
}

export function mapTextualLabResultDocumentItem(item: TextualLabResultDocumentItem): RawCategoricalLabResult {
  return {
    id: item.sancareId,
    creationDate: item.date as string,
    code: item.code,
    conclusion: item.conclusion,
    title: item.title,
    value: item.value,
    documentSourceIdentifier: item.documentSourceIdentifier,
  }
}

export function mergeStayDocuments<T extends { reports: { generatedDataId?: string | null }[], textualHealthEntries: { generatedDataId?: string | null }[] }>(stay: T, documents: ListStayDocumentsResponse): T {
  return {
    ...stay,
    reports: applyDocumentIdentity(stay.reports, documents.textualDocuments, (doc) => ({ documentTitle: doc.title, documentTemplate: doc.type, creationDate: doc.date })),
    textualHealthEntries: applyDocumentIdentity(stay.textualHealthEntries, documents.textualHealthEntries, (doc) => ({ type: doc.type, creationDate: doc.date })),
    drugEvents: documents.drugs.map(mapDrugDocumentItem),
    healthConstants: documents.healthConstants.map(mapHealthConstantDocumentItem),
    labResults: documents.labResults.map(mapLabResultDocumentItem),
    categoricalLabResults: documents.textualLabResults.map(mapTextualLabResultDocumentItem),
  }
}

// Keyed by generatedDataId (== doc.crcs) so it can be applied uniformly whether the chunks come from
// a fresh /documents fetch or are carried over from the previously displayed stay (see reuseStayChunks).
function chunksByCrcs(documents: TextualDocumentItem[]): Map<string, Chunk[]> {
  return new Map(documents.map((doc) => [doc.crcs, buildChunks(doc.content)]))
}

function reusedChunksByGeneratedDataId<T extends { generatedDataId?: string | null, chunks?: Chunk[] }>(entries: T[]): Map<string, Chunk[]> {
  return new Map(entries.filter((entry) => entry.chunks).map((entry) => [entry.generatedDataId ?? '', entry.chunks as Chunk[]]))
}

// Applied after the stay parsing resolves, to graft the backend-provided chunks onto the
// reports/textualHealthEntries — the source of truth for BUILD-264, see ReportDisplayer.vue/chunkJustifications.ts.
function overrideChunks<T extends { generatedDataId?: string | null, chunks?: Chunk[] }>(entries: T[], chunksByGeneratedDataId: Map<string, Chunk[]>): T[] {
  return entries.map((entry) => {
    const chunks = chunksByGeneratedDataId.get(entry.generatedDataId ?? '')

    return chunks ? { ...entry, chunks } : entry
  })
}

export function overrideStayChunks<T extends { reports: { generatedDataId?: string | null, chunks?: Chunk[] }[], textualHealthEntries: { generatedDataId?: string | null, chunks?: Chunk[] }[] }>(stay: T, chunksByGeneratedDataId: Map<string, Chunk[]>): T {
  return {
    ...stay,
    reports: overrideChunks(stay.reports, chunksByGeneratedDataId),
    textualHealthEntries: overrideChunks(stay.textualHealthEntries, chunksByGeneratedDataId),
  }
}

// Neither the documents nor the justifications of a stay change as a result of coding it (diagnoses,
// status, automation...), so instead of re-fetching them on every mutation, the content already merged
// into the previously displayed stay is carried over (matched back onto the entries by generatedDataId).
// Identity fields (documentTitle, documentTemplate, creationDate, id...) are never in the mutation response, so the previous entry is spread first and the fresh one on top.
function reuseContent<T extends { generatedDataId?: string | null }>(entries: T[], previousEntries: T[]): T[] {
  const previousByGeneratedDataId = new Map(previousEntries.map((entry) => [entry.generatedDataId ?? '', entry]))
  return entries.map((entry) => ({ ...previousByGeneratedDataId.get(entry.generatedDataId ?? ''), ...entry }))
}

// drugEvents/healthConstants/labResults/categoricalLabResults aren't keyed by generatedDataId (they
// have no text content to re-attach) and don't change as a result of coding either, so they're
// carried over wholesale rather than re-fetched from /documents.
export function reuseStayDocuments<T extends StayDocumentEntries>(stay: T, previousStay: StayDocumentEntries): T {
  return {
    ...stay,
    reports: reuseContent(stay.reports, previousStay.reports),
    textualHealthEntries: reuseContent(stay.textualHealthEntries, previousStay.textualHealthEntries),
    drugEvents: previousStay.drugEvents,
    healthConstants: previousStay.healthConstants,
    labResults: previousStay.labResults,
    categoricalLabResults: previousStay.categoricalLabResults,
  }
}

// Same join as reuseStayDocuments (by generatedDataId), for the chunks carried over from the
// previously displayed stay instead of a fresh /documents response.
export function reuseStayChunks(previousStay: StayDocumentEntries): Map<string, Chunk[]> {
  return new Map([
    ...reusedChunksByGeneratedDataId(previousStay.reports),
    ...reusedChunksByGeneratedDataId(previousStay.textualHealthEntries),
  ])
}

export function documentsChunksByGeneratedDataId(documents: ListStayDocumentsResponse): Map<string, Chunk[]> {
  return new Map([
    ...chunksByCrcs(documents.textualDocuments),
    ...chunksByCrcs(documents.textualHealthEntries),
  ])
}

// Routed through Api.get (instead of a bare fetch) so a 401 triggers the same session-expiry
// retry, and a failure surfaces the same error toast, as every other API call in the app.
export function fetchDocuments(url: string, commit: Commit): Promise<ListStayDocumentsResponse> {
  return new Promise((resolve, reject) => {
    Api.get(
      {
        url,
        successMut: (data: ListStayDocumentsResponse) => resolve(data),
        errorMut: () => reject(new Error(`Failed to fetch stay documents (${url})`)),
      },
      commit
    )
  })
}

// POST rather than GET query params: presentContents can come from an unbounded list of
// user-typed keywords, too large in the general case to fit safely in a query string (mirrors
// fetchKeywordJustifications in stayJustifications.ts, same reasoning).
function fetchDocumentsWithPresentContents(url: string, presentContents: string[], types: string[], commit: Commit): Promise<ListStayDocumentsResponse> {
  return new Promise((resolve, reject) => {
    Api.post(
      {
        url,
        body: { presentContents, types },
        successMut: (data: ListStayDocumentsResponse) => resolve(data),
        errorMut: () => reject(new Error(`Failed to fetch stay documents (${url})`)),
      },
      commit
    )
  })
}

// Mirrors buildKeywordSearchParams' values/types so orphan keywords get highlighted in the chunks
// the same way they already do, restricted to the same document type, in the "mots-clés"
// justification badges (see ListMcoStayDocumentsController/ListSmrStayDocumentsController).
function fetchDocumentsForSearch(url: string, currentSearch: CurrentSearchLike, commit: Commit): Promise<ListStayDocumentsResponse> {
  const { values, types } = buildKeywordSearchParams(currentSearch)
  return values.length === 0 ? fetchDocuments(url, commit) : fetchDocumentsWithPresentContents(url, values, types, commit)
}

/**
 * Kicks off the documents fetch by the same idType/id used to fetch the stay itself, so it can
 * run in parallel with (rather than only after) the stay request. The returned promise is threaded
 * into refreshDisplayed*Stay as a fallback for the (rare) case where no previously displayed stay
 * can be reused — e.g. on first load, or when switching to a different stay.
 */
// includeBelowThreshold mirrors prefetchMcoStayJustifications/prefetchMcoStaySuggestions (caller passes true to keep reportViewer highlights consistent with the justification popin's).
export function prefetchMcoStayDocuments(idType: string, stayId: string, currentSearch: CurrentSearchLike, commit: Commit, includeBelowThreshold: boolean): Promise<ListStayDocumentsResponse> {
  return fetchDocumentsForSearch(`/api/stay/${stayId}/documents?idType=${idType}&includeBelowThreshold=${includeBelowThreshold ? 1 : 0}`, currentSearch, commit)
}

export function prefetchSmrStayDocuments(idType: string, stayId: string, currentSearch: CurrentSearchLike, commit: Commit): Promise<ListStayDocumentsResponse> {
  return fetchDocumentsForSearch(`/api/smr/stay/${stayId}/documents?idType=${idType}`, currentSearch, commit)
}
